import React, { Component } from "react";
import * as Cookies from "es-cookie";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";

// Self-contained conversation thread for one doubt, shown inside the Doubts-tab
// side drawer (no page navigation). Fetches its own tracking and posts replies —
// reusing the same GraphQL ops as the standalone Doubts page.

const GET_DOUBT_TRACKING = gql`
  query ($doubt_id: Int!) {
    getDoubtTracking(doubt_id: $doubt_id) {
      id
      answer
      image_path
      name
      doubts_user_type
      timestamp
    }
  }
`;

const ADD_DOUBT_ANSWER = gql`
  mutation ($params: DoubtTrackingInput!) {
    addDoubtAnswer(params: $params)
  }
`;

class DoubtThreadDrawer extends Component {
  state = { tracking: [], loading: false, reply: "", sending: false, error: "" };

  componentDidMount() {
    if (this.props.doubtId != null) this.fetchTracking();
  }

  // Refetch when a different doubt is selected while the drawer is open.
  componentDidUpdate(prevProps) {
    if (prevProps.doubtId !== this.props.doubtId && this.props.doubtId != null) {
      this.setState({ reply: "", error: "" }, this.fetchTracking);
    }
  }

  fetchTracking = async () => {
    this.setState({ loading: true });
    try {
      const res = await this.props.client.query({
        query: GET_DOUBT_TRACKING,
        variables: { doubt_id: parseInt(this.props.doubtId, 10) },
        fetchPolicy: "network-only",
      });
      this.setState({ tracking: res?.data?.getDoubtTracking || [], loading: false }, this.scrollToBottom);
    } catch (e) {
      console.log(e);
      this.setState({ tracking: [], loading: false });
    }
  };

  scrollToBottom = () => {
    if (this.scrollRef) this.scrollRef.scrollTop = this.scrollRef.scrollHeight;
  };

  sendReply = async (e) => {
    if (e) e.preventDefault();
    const text = this.state.reply.trim();
    if (!text || this.state.sending) return;
    this.setState({ sending: true, error: "" });
    try {
      await this.props.client.mutate({
        mutation: ADD_DOUBT_ANSWER,
        variables: {
          params: {
            student_user_exam_id: parseInt(Cookies.get("student_user_exam_id")),
            doubts_id: parseInt(this.props.doubtId, 10),
            answer: text,
            name: Cookies.get("studentusername") || "Student",
            image_path: "",
            doubts_user_type: 1,
            estatus: 1,
          },
        },
      });
      this.setState({ reply: "" });
      await this.fetchTracking();
      if (this.props.onReplied) this.props.onReplied();
    } catch (err) {
      console.log(err);
      this.setState({ error: "Couldn't send. Try again." });
    } finally {
      this.setState({ sending: false });
    }
  };

  render() {
    const { tracking, loading, reply, sending, error } = this.state;

    return (
      <div className="sp-thread">
        {/* messages */}
        <div className="sp-thread-scroll" ref={(el) => (this.scrollRef = el)}>
          {loading && tracking.length === 0 ? (
            <div className="text-center text-muted py-5" style={{ fontSize: 13 }}>
              <i className="fas fa-circle-notch fa-spin mr-2" />Loading conversation…
            </div>
          ) : tracking.length === 0 ? (
            <div className="text-center text-muted d-flex flex-column align-items-center justify-content-center h-100 py-5">
              <div style={{ fontSize: "2.4rem", opacity: 0.45, marginBottom: 10 }}>💬</div>
              <div className="font-weight-bold" style={{ color: "#64748b" }}>No replies yet</div>
              <div style={{ fontSize: 12.5, maxWidth: 220 }}>Send a message to start the conversation with the faculty.</div>
            </div>
          ) : (
            tracking.map((t) => {
              const isStudent = t.doubts_user_type === 1;
              return (
                <div key={t.id} className={`sp-msg ${isStudent ? "is-me" : "is-them"}`}>
                  <div className="sp-msg-sender">
                    {t.name || (isStudent ? "You" : "Faculty")}
                    <span className="text-muted"> · {isStudent ? "You" : "Faculty"}</span>
                  </div>
                  <div className={`sp-bubble ${isStudent ? "sp-bubble-me" : "sp-bubble-them"}`}>
                    {t.image_path ? (
                      <a href={t.image_path} target="_blank" rel="noopener noreferrer" className="d-block mb-1">
                        <img src={t.image_path} alt="attachment" style={{ maxWidth: "100%", maxHeight: 160, borderRadius: 8 }} />
                      </a>
                    ) : null}
                    {t.answer && t.answer.trim() ? <div>{t.answer}</div> : null}
                    <div className="sp-msg-time">{t.timestamp}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* reply */}
        <form className="sp-thread-input" onSubmit={this.sendReply}>
          {error ? <div className="text-danger mb-1" style={{ fontSize: 12 }}>{error}</div> : null}
          <div className="d-flex align-items-end" style={{ gap: 8 }}>
            <textarea
              rows={1}
              value={reply}
              disabled={sending}
              placeholder="Type your reply…"
              onChange={(e) => this.setState({ reply: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  this.sendReply();
                }
              }}
            />
            <button type="submit" className="sp-thread-send" disabled={sending || !reply.trim()}>
              {sending ? <i className="fas fa-circle-notch fa-spin" /> : <i className="fas fa-paper-plane" />}
            </button>
          </div>
        </form>
      </div>
    );
  }
}

export default withApollo(DoubtThreadDrawer);
